import { useEffect } from 'react';

const SITE = 'The Full Impact';
const BASE = 'https://thefullimpact.org';
const SCHEMA_ID = 'tf-page-schema';

/**
 * Lightweight per-page SEO updater (no react-helmet dependency).
 * Updates <title>, <meta name="description">, canonical, OG/Twitter tags,
 * and injects a per-page WebPage + BreadcrumbList JSON-LD block.
 *
 * `breadcrumbs` — optional array of { name, path } items, root-first.
 *   If omitted, we auto-build ["Home" → this page].
 * `schemaType` — optional Schema.org @type for the WebPage node
 *   (e.g. "WebPage", "AboutPage", "FAQPage", "ContactPage").
 */
export default function SEO({
  title,
  description,
  path,
  breadcrumbs,
  schemaType = 'WebPage',
}) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const fullTitle = title ? `${title} — ${SITE}` : SITE;
    document.title = fullTitle;

    const setMeta = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    if (description) {
      setMeta('meta[name="description"]', 'name', 'description', description);
      setMeta('meta[property="og:description"]', 'property', 'og:description', description);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);

    const canonicalUrl = `${BASE}${path || ''}`;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);

    // Per-page structured data — WebPage + BreadcrumbList.
    // Removed on unmount / replaced on nav so we never leak stale schema.
    const crumbs =
      Array.isArray(breadcrumbs) && breadcrumbs.length > 0
        ? breadcrumbs
        : [
            { name: 'Home', path: '/' },
            ...(path && path !== '/' && title
              ? [{ name: title, path }]
              : []),
          ];

    const graph = [
      {
        '@type': schemaType,
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: fullTitle,
        ...(description ? { description } : {}),
        inLanguage: 'en-GB',
        isPartOf: { '@id': `${BASE}/#website` },
        isAccessibleForFree: true,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumbs`,
        itemListElement: crumbs.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.name,
          item: `${BASE}${c.path}`,
        })),
      },
    ];

    const payload = { '@context': 'https://schema.org', '@graph': graph };

    let script = document.getElementById(SCHEMA_ID);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = SCHEMA_ID;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(payload);
  }, [title, description, path, breadcrumbs, schemaType]);

  return null;
}
